import { useMemo } from "react";
import { useParams, useSearch } from "@tanstack/react-router";
import {
  useCategories,
  useCategory,
} from "@/features/admin/queries/useCategories";
import { useCategoryAttributesWithValues } from "@/features/admin/queries/useCategoryAttributesWithValues";
import { useTags } from "@/features/admin/queries/useTags";
import { useGallery } from "../gallery/useGallery";
import { mapGalleryItemToFileItem } from "../../utils/galleryUtils";
import { findCategoryBySlug } from "../../utils/categoryUtils";
import type {
  FileExplorerFilters,
  FileExplorerSearchParams,
} from "../../types";
import type { GalleryRequest } from "@/services/api/gallery";
import type { CategoryAttributeResponse } from "@/services/api";

interface UseFileExplorerDataOptions {
  currentPage: number;
  itemsPerPage: number;
}

export function useFileExplorerData({
  currentPage,
  itemsPerPage,
}: UseFileExplorerDataOptions) {
  const { categoryName } = useParams({ from: "/file-explorer/$categoryName" });
  const search = useSearch({
    from: "/file-explorer/$categoryName",
  }) as FileExplorerSearchParams;

  const { data: categories, isLoading: isLoadingCategories } = useCategories();

  const matchedCategory = useMemo(
    () => findCategoryBySlug(categories, categoryName),
    [categories, categoryName]
  );

  const {
    data: category,
    isLoading: isLoadingCategory,
    error: categoryError,
  } = useCategory(matchedCategory?.id || "", {
    enabled: !!matchedCategory?.id,
  });

  const { data: allCategoryAttributes = [] } = useCategoryAttributesWithValues({
    enabled: !!category?.id,
  });

  const { data: allTags = [] } = useTags();

  const resolvedCategoryAttributes = useMemo(() => {
    if (!category?.attributes || allCategoryAttributes.length === 0) {
      return [];
    }

    const attributesMap = new Map(
      allCategoryAttributes.map((attr) => [attr.id, attr])
    );

    return category.attributes
      .map((catAttr) => {
        const fullAttr = attributesMap.get(catAttr.attributeId);
        return fullAttr || null;
      })
      .filter((attr): attr is CategoryAttributeResponse => attr !== null);
  }, [category?.attributes, allCategoryAttributes]);

  const filters: FileExplorerFilters = useMemo(() => {
    const baseFilters: FileExplorerFilters = {
      tags: search.tags ? search.tags.split(",").filter(Boolean) : [],
      name: search.name,
    };

    resolvedCategoryAttributes.forEach((attr) => {
      const attrName = attr.name;
      const searchValue = search[attrName];
      if (searchValue !== undefined && searchValue !== null) {
        baseFilters[attrName] = String(searchValue);
      }
    });

    return baseFilters;
  }, [search, resolvedCategoryAttributes]);

  const galleryRequest: GalleryRequest = useMemo(() => {
    const request: GalleryRequest = {
      page: currentPage,
      pageSize: itemsPerPage,
      filters: [],
    };

    if (search.sortBy) {
      request.sortBy = search.sortBy;
    }
    if (search.sortDirection) {
      request.sortDirection = search.sortDirection;
    }

    if (search.name) {
      request.searchTerm = search.name;
      request.searchColumn = "name";
    }

    resolvedCategoryAttributes.forEach((attr) => {
      const attrName = attr.name;
      const filterValue = search[attrName];
      if (filterValue !== undefined && filterValue !== null) {
        request.filters.push({
          attributeId: attr.id,
          values: [String(filterValue)],
        });
      }
    });

    if (filters.tags.length > 0) {
      const tagIds = filters.tags
        .map((tagName) => {
          const tag = allTags.find((t) => t.name === tagName);
          return tag?.id;
        })
        .filter((id): id is number => id !== undefined);

      if (tagIds.length > 0) {
        request.tagFilters = {
          tagIds,
          operator: "OR",
        };
      }
    }

    return request;
  }, [
    currentPage,
    itemsPerPage,
    resolvedCategoryAttributes,
    search,
    filters.tags,
    allTags,
  ]);

  const {
    data: galleryData,
    isLoading: isLoadingGallery,
    error: galleryError,
  } = useGallery(matchedCategory?.id || "", galleryRequest, {
    enabled: !!matchedCategory?.id,
  });

  const allFiles = useMemo(() => {
    if (!galleryData?.items) return [];
    return galleryData.items.map(mapGalleryItemToFileItem);
  }, [galleryData]);

  const totalPages = galleryData?.totalPages || 1;
  const totalFiles = galleryData?.totalCount || 0;

  const isLoading =
    isLoadingCategories || isLoadingCategory || isLoadingGallery;

  return {
    matchedCategory,
    category,
    categoryError,
    categoryAttributes: resolvedCategoryAttributes,
    filters,
    allFiles,
    totalPages,
    totalFiles,
    isLoading,
    isLoadingCategories,
    isLoadingCategory,
    isLoadingGallery,
    galleryError,
  };
}
